import { useCallback } from 'react'
import { ActionType, treesDispatchAtom, treesStateAtom } from './reducer'
import { useGetTree } from './trees.hooks'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'

const treeKey = ({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) => `${treeId}:${versionId}`

export function useResetTree() {
  const dispatch = useSetAtom(treesDispatchAtom)

  return useCallback(
    ({ treeId, versionId }: { treeId: string; versionId: string }) => {
      dispatch({ type: ActionType.ResetTree, treeId, versionId })
    },
    [dispatch],
  )
}

export function useResetForTree(ids?: { tree_id: string; version: string }) {
  const resetTree = useResetTree()
  return useCallback(() => {
    if (!ids) return
    const { tree_id: treeId, version: versionId } = ids
    resetTree({ treeId, versionId })
  }, [ids, resetTree])
}

export function useTreeGetter() {
  const getTree = useGetTree()
  const state = useAtomValue(treesStateAtom) // read‑only
  const dispatch = useSetAtom(treesDispatchAtom) // write‑only
  const resetTree = useResetTree()

  const { retrievedTrees: trees, retrievingTrees: pending, treesById } = state

  return useCallback(
    ({
      treeId,
      versionId,
      depth,
    }: {
      treeId: string
      versionId: string
      depth?: number
    }) => {
      const tid = treeKey({ treeId, versionId })
      const justInitiatedFetch = !trees.includes(tid) && !pending.includes(tid)
      if (justInitiatedFetch) {
        dispatch({ type: ActionType.RetrievingTree, treeId, versionId })
        getTree({ treeId, versionId, depth }).then((tree) => {
          dispatch({
            type: ActionType.RetrievedTree,
            treeId,
            versionId,
            tree,
          })
        })
      }
      const tree = treesById[tid]
      if (tree) {
        return {
          tree,
          resetTree: () => resetTree({ treeId, versionId }),
          retrieving: false,
        }
      }
      return {
        tree: undefined,
        resetTree: undefined,
        retrieving: justInitiatedFetch || pending.includes(tid),
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trees, pending, treesById, dispatch],
  )
}

// useGetTreeById returns one tree and a function that will remove that tree
// from state so that it would be re-retrieved
export function useGetTreeById({
  treeId,
  versionId,
  depth,
}: {
  treeId?: string
  versionId?: string
  depth?: number
}) {
  const getter = useTreeGetter()
  if (!treeId || !versionId)
    return {
      tree: undefined,
      resetTree: undefined,
      retrieving: false,
    }
  return getter({ treeId, versionId, depth })
}
